import { memo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from 'types';
import api from 'app/api';
import { receiveError } from 'utils/showSuccessErrorModal';
import { useTheme } from 'utils/context/ThemeProvider';
import { getDomainConfig } from 'utils/whiteLabel';
import { getActiveToken } from 'utils/tabAuth';
import {
  CHANGE_EVENT,
  belongsToEmployee,
  buildTestWidgetProps,
  ensureActiveTestUser,
  getActiveTestUser,
  subscribeTestWidget,
  type ActiveTestUser,
  type TestWidgetMountParams,
} from './helpers/testWidgetUsers';

const STORAGE_KEY = 'heltar:test-widget';
export { subscribeTestWidget };

export const isTestWidgetOn = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

export const setTestWidgetOn = (on: boolean): void => {
  try {
    if (on) localStorage.setItem(STORAGE_KEY, '1');
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
};

const CONFIG_KEY = 'heltar:test-widget-config';

export const getTestWidgetConfig = (): Record<string, unknown> => {
  try {
    const raw = localStorage.getItem(CONFIG_KEY);
    return raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
  } catch {
    return {};
  }
};

export const setTestWidgetConfig = (
  config: Record<string, unknown> | null,
): void => {
  try {
    if (config && Object.keys(config).length)
      localStorage.setItem(CONFIG_KEY, JSON.stringify(config));
    else localStorage.removeItem(CONFIG_KEY);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
};

export type WidgetWindow = Window & {
  HeltarChat?: {
    initBubble: (props: unknown) => void;
    unmount: () => void;
    setMode?: (mode: 'light' | 'dark' | 'system') => void;
  };
};

// Serialise unpin → pin on the same per-visitor key: a slow DELETE from the
// previous mount must not erase the fresh pin.
let pendingUnpin: Promise<unknown> = Promise.resolve();

const ensureWidgetScript = (win: WidgetWindow, doc: Document): Promise<void> =>
  new Promise((resolve, reject) => {
    if (win.HeltarChat) return resolve();
    const existing = doc.querySelector<HTMLScriptElement>(
      'script[data-heltarchat-test-widget]',
    );
    // A tag whose error already fired never calls these listeners again, and
    // the promise the caller chains `pendingUnpin` onto would never settle —
    // deadlocking every later mount in the tab. Drop it and re-inject instead.
    if (existing?.dataset.failed) existing.remove();
    else if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () =>
        reject(new Error('Failed to load widget bundle')),
      );
      return;
    }
    const script = doc.createElement('script');
    script.src = `/web-widget.js?t=${Date.now()}`;
    script.defer = true;
    script.setAttribute('data-heltarchat-test-widget', 'true');
    script.onload = () => resolve();
    script.onerror = () => {
      script.dataset.failed = 'true';
      reject(new Error('Failed to load /web-widget.js from this dashboard'));
    };
    doc.body.appendChild(script);
  });

/** Loads the widget bundle into `doc`, pins the bot under test (if any) for
 *  this visitor and mounts the bubble on `win`. Returns the teardown: unmount
 *  + unpin, chained so the DELETE can never overtake the POST. Shared by the
 *  floating launcher (window/document) and the playground preview (iframe).
 *  Pinned before the widget mounts so the first message/greeting already
 *  routes to the bot under test. */
/** Pin a bot to a test visitor. The server also snapshots the editor's
 *  "$LATEST" here, so re-calling this after a save is what keeps the preview
 *  on the newest config. */
export const pinTestBot = async (
  chatbotId: string,
  visitorId: string,
): Promise<void> => {
  // Same serialisation `mountTestWidget` uses: a teardown's DELETE must not
  // overtake this POST and erase the fresh pin.
  await pendingUnpin;
  await api
    .post(`/chatbots/${chatbotId}/web-test-session`, { visitorId })
    .catch(error => {
      receiveError(
        (error as { response?: { data?: unknown } })?.response?.data,
      );
      throw error;
    });
};

export const mountTestWidget = (
  win: WidgetWindow,
  doc: Document,
  params: TestWidgetMountParams,
): (() => void) => {
  let cancelled = false;
  const sessionPath = `/chatbots/${params.chatbotId}/web-test-session`;
  const visitorId = params.activeUser.id;
  const pinned = ensureWidgetScript(win, doc).then(async () => {
    if (!params.chatbotId || cancelled) return false;
    await pendingUnpin;
    if (cancelled) return false;
    await pinTestBot(params.chatbotId, visitorId);
    return true;
  });
  // Only mount once the pin holds: an unpinned mount would answer from the
  // business's LIVE bot while the pane still names the bot under test.
  void pinned
    .then(ok => {
      if (ok && !cancelled)
        win.HeltarChat?.initBubble(buildTestWidgetProps(params));
    })
    .catch(() => {
      /* pin failed — receiveError already surfaced it */
    });
  return () => {
    cancelled = true;
    try {
      win.HeltarChat?.unmount();
    } catch {
      /* ignore */
    }
    // Server TTL is the backstop.
    pendingUnpin = pinned
      .then(ok =>
        ok ? api.delete(sessionPath, { data: { visitorId } }) : undefined,
      )
      .catch(() => {});
  };
};

export const TestWidgetHost = memo(function TestWidgetHost() {
  const businessId = useSelector((s: RootState) => s.authPage?.business?.id);
  const employeeId = useSelector(
    (s: RootState) => s.authPage?.currentEmployee?.id,
  );
  const employeeName = useSelector(
    (s: RootState) => s.authPage?.currentEmployee?.name,
  );
  const { theme } = useTheme();
  const [enabled, setEnabled] = useState<boolean>(isTestWidgetOn());
  const [config, setConfig] = useState<Record<string, unknown>>(
    getTestWidgetConfig(),
  );
  const [activeUser, setActiveUser] = useState<ActiveTestUser | null>(
    getActiveTestUser(),
  );

  useEffect(
    () =>
      subscribeTestWidget(() => {
        setEnabled(isTestWidgetOn());
        setConfig(getTestWidgetConfig());
        setActiveUser(getActiveTestUser());
      }),
    [],
  );

  const active = enabled && !!businessId && !!employeeId;

  useEffect(() => {
    if (!active || !businessId || !employeeId) return;
    const cur = getActiveTestUser();
    if (cur && belongsToEmployee(cur.id, employeeId)) return;
    void ensureActiveTestUser(businessId, employeeId);
  }, [active, businessId, employeeId]);

  const configKey = JSON.stringify(config);

  const testChatbotId =
    typeof config.chatbotId === 'string' ? config.chatbotId : undefined;
  const testBotName =
    typeof config.botName === 'string' ? config.botName : undefined;

  useEffect(() => {
    if (
      !active ||
      !businessId ||
      !employeeId ||
      !activeUser ||
      !belongsToEmployee(activeUser.id, employeeId)
    )
      return;
    return mountTestWidget(window as WidgetWindow, document, {
      businessId,
      apiHost: getDomainConfig().apiUrl,
      theme,
      config,
      activeUser,
      employeeName,
      hash: getActiveToken(),
      chatbotId: testChatbotId,
      botName: testBotName,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    active,
    businessId,
    employeeId,
    employeeName,
    configKey,
    activeUser?.id,
    activeUser?.name,
  ]);

  useEffect(() => {
    if (!active) return;
    (window as WidgetWindow).HeltarChat?.setMode?.(theme);
  }, [theme, active]);

  return null;
});
